import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { eventsAPI, eventSeriesAPI, uploadAPI, userSettingsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ComboboxWithAdd } from '../components/ui/combobox-with-add';
import { EVENT_TYPES, EVENT_SIZES, US_STATES, cn } from '../lib/utils';
import { toast } from 'sonner';
import { format, addYears } from 'date-fns';
import { CalendarIcon, Loader2, Plus, ArrowLeft, Upload, X, Image, Users, Clock, History, AlertCircle } from 'lucide-react';
import { Switch } from '../components/ui/switch';

const DEFAULT_VENUE_TYPES = [
  "Indoor", "Outdoor", "Expo Center", "Fairgrounds", 
  "Community Center", "Church/School", "Park", "Street Festival"
];

export const AddEvent = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [customCategory, setCustomCategory] = useState('');
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [venueTypes, setVenueTypes] = useState(DEFAULT_VENUE_TYPES);
  const [prefillSeriesId, setPrefillSeriesId] = useState(null);
  const [prefillSeriesName, setPrefillSeriesName] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    event_type: '',
    location: '',
    city: '',
    state: '',
    date: null,
    end_date: null,
    date_hours: '',  // Hours for primary date range (e.g., "9am-5pm")
    start_time: '',  // Daily start time (HH:MM)
    end_time: '',    // Daily end time (HH:MM)
    event_dates: [], // Array of {start_date, end_date, hours}
    deadline: null,
    booth_cost: '',
    event_size: '',
    venue_type: '',
    description: '',
    website_url: '',
    signup_url: '',  // Application/signup URL
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    image_url: '',
    is_community: false,  // Share with AMSCompass community
    series_id: null,  // Link to series
    recurring_schedule: null,  // For farmers markets: {days: [], frequency: "every"}
  });

  // Recurring schedule state for farmers markets
  const [recurringDays, setRecurringDays] = useState([]);
  const [recurringFrequency, setRecurringFrequency] = useState('every');

  const isFarmersMarket = formData.event_type === 'farmers_market';

  const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const FREQUENCY_OPTIONS = [
    { value: 'every', label: 'Every week' },
    { value: 'every_2nd', label: 'Every 2nd week' },
    { value: 'every_3rd', label: 'Every 3rd week' },
    { value: '1st_and_3rd', label: '1st & 3rd of the month' },
    { value: '2nd_and_4th', label: '2nd & 4th of the month' },
  ];

  // Compute preview of recurring dates client-side
  const computeRecurringPreview = () => {
    if (!isFarmersMarket || !formData.date || !formData.end_date || recurringDays.length === 0) return [];
    const start = new Date(format(formData.date, 'yyyy-MM-dd') + 'T00:00:00');
    const end = new Date(format(formData.end_date, 'yyyy-MM-dd') + 'T00:00:00');
    if (end < start) return [];
    const result = [];
    const current = new Date(start);
    while (current <= end) {
      const jsDay = current.getDay(); // 0=Sun, 1=Mon...6=Sat
      const pyDay = jsDay === 0 ? 6 : jsDay - 1; // Convert to 0=Mon...6=Sun
      if (recurringDays.includes(pyDay)) {
        let include = false;
        if (recurringFrequency === 'every') include = true;
        else if (recurringFrequency === 'every_2nd' || recurringFrequency === 'every_3rd') {
          const daysDiff = Math.floor((current - start) / (1000 * 60 * 60 * 24));
          const weekNum = Math.floor(daysDiff / 7);
          const skip = recurringFrequency === 'every_2nd' ? 2 : 3;
          include = weekNum % skip === 0;
        } else if (recurringFrequency === '1st_and_3rd' || recurringFrequency === '2nd_and_4th') {
          const weekOfMonth = Math.floor((current.getDate() - 1) / 7) + 1;
          include = recurringFrequency === '1st_and_3rd' ? [1, 3].includes(weekOfMonth) : [2, 4].includes(weekOfMonth);
        }
        if (include) result.push(new Date(current));
      }
      current.setDate(current.getDate() + 1);
    }
    return result;
  };

  const recurringPreview = computeRecurringPreview();

  // Add additional date range
  const addDateRange = () => {
    setFormData(prev => ({
      ...prev,
      event_dates: [...prev.event_dates, { start_date: null, end_date: null, hours: '' }]
    }));
  };

  // Remove date range
  const removeDateRange = (index) => {
    setFormData(prev => ({
      ...prev,
      event_dates: prev.event_dates.filter((_, i) => i !== index)
    }));
  };

  // Update date range
  const updateDateRange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      event_dates: prev.event_dates.map((range, i) => 
        i === index ? { ...range, [field]: value } : range
      )
    }));
  };

  useEffect(() => {
    const fetchVenueTypes = async () => {
      try {
        const response = await userSettingsAPI.get('venue_types');
        setVenueTypes(response.data.values || DEFAULT_VENUE_TYPES);
      } catch (error) {
        console.error('Failed to fetch venue types');
      }
    };
    fetchVenueTypes();
  }, []);

  // Load prefill data from query params (for "Add Next Year's Occurrence")
  useEffect(() => {
    const seriesId = searchParams.get('series');
    const prefillEventId = searchParams.get('prefill');
    
    if (seriesId && prefillEventId) {
      const loadPrefillData = async () => {
        try {
          // Get the source event to prefill from
          const eventRes = await eventsAPI.getOne(prefillEventId);
          const sourceEvent = eventRes.data;
          
          // Get series info
          const seriesRes = await eventSeriesAPI.get(seriesId);
          const series = seriesRes.data;
          
          setPrefillSeriesId(seriesId);
          setPrefillSeriesName(series.name);
          
          // Prefill form with source event data (but update dates to next year)
          const sourceDate = sourceEvent.date ? new Date(sourceEvent.date) : null;
          const nextYearDate = sourceDate ? addYears(sourceDate, 1) : null;
          
          const sourceEndDate = sourceEvent.end_date ? new Date(sourceEvent.end_date) : null;
          const nextYearEndDate = sourceEndDate ? addYears(sourceEndDate, 1) : null;
          
          // Remove year from title if present
          let prefillTitle = sourceEvent.title || '';
          prefillTitle = prefillTitle.replace(/\s*(20\d{2}|'\d{2})\s*/gi, ' ').trim();
          // Add next year
          if (nextYearDate) {
            prefillTitle = `${prefillTitle} ${nextYearDate.getFullYear()}`;
          }
          
          setFormData(prev => ({
            ...prev,
            title: prefillTitle,
            event_type: sourceEvent.event_type || '',
            location: sourceEvent.location || '',
            city: sourceEvent.city || '',
            state: sourceEvent.state || '',
            date: nextYearDate,
            end_date: nextYearEndDate,
            event_size: sourceEvent.event_size || '',
            venue_type: sourceEvent.venue_type || '',
            description: sourceEvent.description || '',
            series_id: seriesId,
          }));
          
          toast.success(`Pre-filled from ${series.name} - update dates and details for the new occurrence`);
        } catch (error) {
          console.error('Failed to load prefill data:', error);
          toast.error('Failed to load event data for prefilling');
        }
      };
      
      loadPrefillData();
    }
  }, [searchParams]);

  const handleAddVenueType = async (newType) => {
    try {
      const response = await userSettingsAPI.addValue('venue_types', newType);
      setVenueTypes(response.data.values);
      toast.success(`Venue type "${newType}" added`);
    } catch (error) {
      toast.error('Failed to add venue type');
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Handle custom category selection
    if (field === 'event_type') {
      if (value === 'custom') {
        setShowCustomCategory(true);
      } else {
        setShowCustomCategory(false);
        setCustomCategory('');
      }
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      handleChange('image_url', response.data.url);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    handleChange('image_url', '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please log in to add events');
      navigate('/login');
      return;
    }

    // Validate custom category
    const eventType = formData.event_type === 'custom' ? customCategory.trim() : formData.event_type;
    if (!eventType) {
      toast.error('Please select or enter an event category');
      return;
    }

    if (!formData.title || !formData.city || !formData.state || !formData.date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // Format event dates
      const formattedEventDates = formData.event_dates
        .filter(d => d.start_date)
        .map(d => ({
          start_date: format(d.start_date, 'yyyy-MM-dd'),
          end_date: d.end_date ? format(d.end_date, 'yyyy-MM-dd') : null
        }));

      // Build event_hours string from per-day hours
      const hoursEntries = [];
      if (formData.date && formData.date_hours) {
        const dateLabel = formData.end_date 
          ? `${format(formData.date, 'EEE MMM d')}-${format(formData.end_date, 'd')}`
          : format(formData.date, 'EEE MMM d');
        hoursEntries.push(`${dateLabel}: ${formData.date_hours}`);
      }
      formData.event_dates.forEach(d => {
        if (d.start_date && d.hours) {
          const dateLabel = d.end_date
            ? `${format(d.start_date, 'EEE MMM d')}-${format(d.end_date, 'd')}`
            : format(d.start_date, 'EEE MMM d');
          hoursEntries.push(`${dateLabel}: ${d.hours}`);
        }
      });
      const event_hours = hoursEntries.length > 0 ? hoursEntries.join(', ') : null;

      const eventData = {
        ...formData,
        event_type: eventType,
        date: formData.date ? format(formData.date, 'yyyy-MM-dd') : null,
        end_date: formData.end_date ? format(formData.end_date, 'yyyy-MM-dd') : null,
        event_dates: formattedEventDates.length > 0 ? formattedEventDates : null,
        deadline: formData.deadline ? format(formData.deadline, 'yyyy-MM-dd') : null,
        booth_cost: formData.booth_cost ? parseFloat(formData.booth_cost) : null,
        event_hours,
        start_time: formData.start_time || null,
        end_time: formData.end_time || null,
        source: 'user_created',
        recurring_schedule: isFarmersMarket && recurringDays.length > 0 ? {
          days: recurringDays,
          frequency: recurringFrequency,
        } : null,
      };
      
      // Remove internal-only fields
      delete eventData.date_hours;
      
      const res = await eventsAPI.create(eventData);
      toast.success('Event created successfully!');
      navigate(`/events/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="card-dashboard text-center py-12 max-w-md">
          <Plus className="w-16 h-16 text-[#71717A] mx-auto mb-4" />
          <h2 className="text-2xl font-bold font-['Outfit'] text-[#1E3A5F] mb-2">
            Login Required
          </h2>
          <p className="text-[#4A4A4A] mb-6">
            Please log in to add new events
          </p>
          <Link to="/login">
            <Button className="btn-primary">Log in</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6 md:px-12">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/events"
          className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#1E3A5F] mb-6 font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to events
        </Link>

        <div className="card-dashboard" style={{ boxShadow: '6px 6px 0px 0px #1E3A5F' }}>
          <div className="mb-8">
            <h1 className="text-3xl font-bold font-['Outfit'] text-[#1E3A5F] mb-2">
              {prefillSeriesName ? `Add ${prefillSeriesName} Occurrence` : 'Add New Event'}
            </h1>
            <p className="text-[#4A4A4A]">
              {prefillSeriesName 
                ? 'Pre-filled from previous year. Update dates and details for this occurrence.'
                : 'Share an event opportunity with the vendor community'
              }
            </p>
          </div>

          {/* Series Link Notice */}
          {prefillSeriesId && (
            <div className="mb-6 p-4 bg-[#1E3A5F]/10 rounded-xl border-2 border-[#1E3A5F]/30 flex items-center gap-3">
              <History className="w-5 h-5 text-[#1E3A5F]" />
              <div className="flex-1">
                <p className="text-sm font-medium text-[#1E3A5F]">
                  Adding to series: {prefillSeriesName}
                </p>
                <p className="text-xs text-gray-600">
                  This event will automatically be linked to the series when saved.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Info */}
            <div className="space-y-6">
              <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] pb-2 border-b-2 border-[#E5E5E5]">
                Basic Information
              </h2>
              
              <div className="space-y-2">
                <Label htmlFor="title" className="font-medium text-[#1E3A5F]">
                  Event Title <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="title"
                  placeholder="e.g., Austin Craft Fair 2025"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className="input-default w-full"
                  data-testid="add-event-title"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">
                    Event Type <span className="text-red-500">*</span>
                  </Label>
                  <Select value={formData.event_type} onValueChange={(v) => handleChange('event_type', v)}>
                    <SelectTrigger className="input-default" data-testid="add-event-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-[#1E3A5F] rounded-xl bg-white">
                      {Object.entries(EVENT_TYPES).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.label}</SelectItem>
                      ))}
                      <SelectItem value="custom">+ Add Custom Category</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {showCustomCategory && (
                  <div className="space-y-2">
                    <Label htmlFor="customCategory" className="font-medium text-[#1E3A5F]">
                      Custom Category Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="customCategory"
                      placeholder="e.g., Food Festival, Flea Market"
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      className="input-default w-full"
                      data-testid="add-event-custom-category"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">Event Size</Label>
                  <Select value={formData.event_size} onValueChange={(v) => handleChange('event_size', v)}>
                    <SelectTrigger className="input-default" data-testid="add-event-size">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-[#1E3A5F] rounded-xl bg-white">
                      {Object.entries(EVENT_SIZES).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">Venue Type</Label>
                  <ComboboxWithAdd
                    options={venueTypes}
                    value={formData.venue_type}
                    onValueChange={(v) => handleChange('venue_type', v)}
                    onAddNew={handleAddVenueType}
                    placeholder="Select venue type"
                    searchPlaceholder="Search or add venue type..."
                    addNewText="Add venue type"
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="space-y-6">
              <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] pb-2 border-b-2 border-[#E5E5E5]">
                Location
              </h2>

              <div className="space-y-2">
                <Label htmlFor="location" className="font-medium text-[#1E3A5F]">Venue Address</Label>
                <Input
                  id="location"
                  placeholder="e.g., Austin Convention Center, 500 E Cesar Chavez St"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  className="input-default w-full"
                  data-testid="add-event-location"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="city" className="font-medium text-[#1E3A5F]">
                    City <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="city"
                    placeholder="e.g., Austin"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    className="input-default w-full"
                    data-testid="add-event-city"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">
                    State <span className="text-red-500">*</span>
                  </Label>
                  <Select value={formData.state} onValueChange={(v) => handleChange('state', v)}>
                    <SelectTrigger className="input-default" data-testid="add-event-state">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-[#1E3A5F] rounded-xl max-h-60">
                      {US_STATES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Dates & Cost */}
            <div className="space-y-6">
              <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] pb-2 border-b-2 border-[#E5E5E5]">
                Dates & Cost
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">
                    Start Date <span className="text-red-500">*</span>
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "input-default w-full justify-start text-left font-normal",
                          !formData.date && "text-gray-400"
                        )}
                        data-testid="add-event-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.date ? format(formData.date, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 border-2 border-[#1E3A5F] rounded-xl" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.date}
                        onSelect={(date) => handleChange('date', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "input-default w-full justify-start text-left font-normal",
                          !formData.end_date && "text-gray-400"
                        )}
                        data-testid="add-event-end-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.end_date ? format(formData.end_date, 'PPP') : 'Same as start (optional)'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 border-2 border-[#1E3A5F] rounded-xl" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.end_date}
                        onSelect={(date) => handleChange('end_date', date)}
                        defaultMonth={formData.date || new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Hours for primary date */}
              {formData.date && (
                <div className="space-y-2 mt-4 p-4 bg-[#F5F5F5] rounded-xl border border-[#E5E5E5]">
                  <Label className="font-medium text-[#1E3A5F] flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#1E3A5F]" />
                    Hours for {format(formData.date, 'EEE, MMM d')}{formData.end_date ? ` - ${format(formData.end_date, 'MMM d')}` : ''}
                  </Label>

                  {/* Precise start/end time (24-hour HH:MM) */}
                  <div className="grid grid-cols-2 gap-3 pb-2">
                    <div>
                      <Label htmlFor="add-start-time" className="text-xs text-[#71717A]">Start time</Label>
                      <Input
                        id="add-start-time"
                        type="time"
                        value={formData.start_time}
                        onChange={(e) => handleChange('start_time', e.target.value)}
                        className="input-default w-full mt-1"
                        data-testid="add-event-start-time"
                      />
                    </div>
                    <div>
                      <Label htmlFor="add-end-time" className="text-xs text-[#71717A]">End time</Label>
                      <Input
                        id="add-end-time"
                        type="time"
                        value={formData.end_time}
                        onChange={(e) => handleChange('end_time', e.target.value)}
                        className="input-default w-full mt-1"
                        data-testid="add-event-end-time"
                      />
                    </div>
                  </div>

                  <Label className="text-xs text-[#71717A]">Or describe the hours (optional)</Label>
                  <Input
                    placeholder="e.g., 9am-5pm or Sat 9-5, Sun 10-4"
                    value={formData.date_hours}
                    onChange={(e) => handleChange('date_hours', e.target.value)}
                    className="input-default w-full"
                    data-testid="add-event-date-hours"
                  />
                </div>
              )}

              {/* Farmers Market Recurring Schedule */}
              {isFarmersMarket && formData.date && formData.end_date && (
                <div className="space-y-4 mt-4 p-5 bg-emerald-50 rounded-xl border-2 border-emerald-300" data-testid="recurring-schedule-panel">
                  <div>
                    <Label className="font-bold text-emerald-800 text-base flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-emerald-600" />
                      Market Schedule
                    </Label>
                    <p className="text-xs text-emerald-700 mt-1">
                      Select which days and how often this market runs within {format(formData.date, 'MMM d')} - {format(formData.end_date, 'MMM d, yyyy')}
                    </p>
                  </div>

                  {/* Day of Week Selector */}
                  <div className="space-y-2">
                    <Label className="font-medium text-emerald-800 text-sm">Day(s) of the Week</Label>
                    <div className="flex gap-2 flex-wrap">
                      {DAY_NAMES.map((day, idx) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => {
                            setRecurringDays(prev =>
                              prev.includes(idx) ? prev.filter(d => d !== idx) : [...prev, idx]
                            );
                          }}
                          className={`px-3 py-2 rounded-lg text-sm font-semibold border-2 transition-all ${
                            recurringDays.includes(idx)
                              ? 'bg-emerald-600 border-emerald-600 text-white shadow-md'
                              : 'bg-white border-emerald-200 text-emerald-700 hover:border-emerald-400'
                          }`}
                          data-testid={`recurring-day-${idx}`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Frequency Selector */}
                  <div className="space-y-2">
                    <Label className="font-medium text-emerald-800 text-sm">Frequency</Label>
                    <Select value={recurringFrequency} onValueChange={setRecurringFrequency}>
                      <SelectTrigger className="bg-white border-2 border-emerald-200 rounded-lg" data-testid="recurring-frequency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-2 border-emerald-300 rounded-xl bg-white">
                        {FREQUENCY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Preview */}
                  {recurringDays.length > 0 && (
                    <div className="mt-3 p-3 bg-white rounded-lg border border-emerald-200" data-testid="recurring-preview">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-emerald-800">
                          {recurringPreview.length} market day{recurringPreview.length !== 1 ? 's' : ''}
                        </span>
                        <span className="text-xs text-emerald-600">
                          {recurringDays.map(d => DAY_NAMES[d]).join(', ')} — {FREQUENCY_OPTIONS.find(o => o.value === recurringFrequency)?.label}
                        </span>
                      </div>
                      {recurringPreview.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                          {recurringPreview.slice(0, 20).map((d, i) => (
                            <span key={i} className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                              {format(d, 'MMM d')}
                            </span>
                          ))}
                          {recurringPreview.length > 20 && (
                            <span className="text-xs text-emerald-600 font-medium">+{recurringPreview.length - 20} more</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Additional Date Ranges */}
              {formData.event_dates.length > 0 && (
                <div className="space-y-4 mt-4 p-4 bg-[#FFEDDF]/50 rounded-xl border border-[#1E3A5F]">
                  <Label className="font-medium text-[#1E3A5F]">Additional Dates</Label>
                  {formData.event_dates.map((dateRange, index) => (
                    <div key={index} className="space-y-3 p-3 bg-white rounded-lg border border-[#E5E5E5]">
                      <div className="flex items-center gap-4">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "input-default flex-1 justify-start text-left font-normal",
                                !dateRange.start_date && "text-gray-400"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {dateRange.start_date ? format(dateRange.start_date, 'MMM d, yyyy') : 'Start date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 border-2 border-[#1E3A5F] rounded-xl" align="start">
                            <Calendar
                              mode="single"
                              selected={dateRange.start_date}
                              onSelect={(date) => updateDateRange(index, 'start_date', date)}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <span className="text-gray-500">to</span>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "input-default flex-1 justify-start text-left font-normal",
                                !dateRange.end_date && "text-gray-400"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {dateRange.end_date ? format(dateRange.end_date, 'MMM d, yyyy') : 'End date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 border-2 border-[#1E3A5F] rounded-xl" align="start">
                            <Calendar
                              mode="single"
                              selected={dateRange.end_date}
                              onSelect={(date) => updateDateRange(index, 'end_date', date)}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeDateRange(index)}
                          className="text-red-500 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      {dateRange.start_date && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-[#1E3A5F]" />
                          <Input
                            placeholder="Hours (e.g., 10am-6pm)"
                            value={dateRange.hours || ''}
                            onChange={(e) => updateDateRange(index, 'hours', e.target.value)}
                            className="input-default flex-1"
                            data-testid={`add-event-date-${index}-hours`}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              <Button
                type="button"
                variant="outline"
                onClick={addDateRange}
                className="mt-2 text-[#1E3A5F] border-dashed border-[#1E3A5F]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Additional Dates
              </Button>

              <div className="grid md:grid-cols-2 gap-6 mt-4">
                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">Application Deadline</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "input-default w-full justify-start text-left font-normal",
                          !formData.deadline && "text-gray-400"
                        )}
                        data-testid="add-event-deadline"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.deadline ? format(formData.deadline, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 border-2 border-[#1E3A5F] rounded-xl" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.deadline}
                        onSelect={(date) => handleChange('deadline', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="booth_cost" className="font-medium text-[#1E3A5F]">Booth Cost ($)</Label>
                  <Input
                    id="booth_cost"
                    type="number"
                    placeholder="e.g., 250"
                    value={formData.booth_cost}
                    onChange={(e) => handleChange('booth_cost', e.target.value)}
                    className="input-default w-full"
                    data-testid="add-event-cost"
                  />
                </div>
              </div>
            </div>

            {/* Event Image */}
            <div className="space-y-6">
              <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] pb-2 border-b-2 border-[#E5E5E5]">
                Event Image
              </h2>

              <div className="space-y-4">
                {imagePreview || formData.image_url ? (
                  <div className="relative w-full max-w-md">
                    <img
                      src={imagePreview || formData.image_url}
                      alt="Event preview"
                      className="w-full h-48 object-cover rounded-xl border-2 border-[#1E3A5F]"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={removeImage}
                      data-testid="remove-image-btn"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label
                    className="flex flex-col items-center justify-center w-full max-w-md h-48 border-2 border-dashed border-[#1E3A5F] rounded-xl cursor-pointer hover:bg-[#F5F5F5] transition-colors"
                    data-testid="image-upload-area"
                  >
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleImageUpload}
                      className="hidden"
                      data-testid="image-upload-input"
                    />
                    {uploading ? (
                      <Loader2 className="w-10 h-10 text-[#1E3A5F] animate-spin" />
                    ) : (
                      <>
                        <Image className="w-10 h-10 text-[#1E3A5F] mb-2" />
                        <span className="text-[#4A4A4A] font-medium">Click to upload image</span>
                        <span className="text-sm text-[#71717A]">JPEG, PNG, GIF or WebP (max 5MB)</span>
                      </>
                    )}
                  </label>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-6">
              <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] pb-2 border-b-2 border-[#E5E5E5]">
                Description
              </h2>

              <div className="space-y-2">
                <Label htmlFor="description" className="font-medium text-[#1E3A5F]">Event Description</Label>
                <Textarea
                  id="description"
                  placeholder="Tell vendors about this event..."
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="input-default w-full min-h-[120px] py-3"
                  data-testid="add-event-description"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="website_url" className="font-medium text-[#1E3A5F]">Website URL</Label>
                <Input
                  id="website_url"
                  type="url"
                  placeholder="https://..."
                  value={formData.website_url}
                  onChange={(e) => handleChange('website_url', e.target.value)}
                  className="input-default w-full"
                  data-testid="add-event-website"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup_url" className="font-medium text-[#1E3A5F]">Application/Signup URL</Label>
                <Input
                  id="signup_url"
                  type="url"
                  placeholder="https://... (vendor application link)"
                  value={formData.signup_url}
                  onChange={(e) => handleChange('signup_url', e.target.value)}
                  className="input-default w-full"
                  data-testid="add-event-signup-url"
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] pb-2 border-b-2 border-[#E5E5E5]">
                Contact Information
              </h2>

              <div className="space-y-2">
                <Label htmlFor="contact_name" className="font-medium text-[#1E3A5F]">Contact Name</Label>
                <Input
                  id="contact_name"
                  placeholder="e.g., John Smith"
                  value={formData.contact_name}
                  onChange={(e) => handleChange('contact_name', e.target.value)}
                  className="input-default w-full"
                  data-testid="add-event-contact-name"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="contact_phone" className="font-medium text-[#1E3A5F]">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    type="tel"
                    placeholder="e.g., (555) 123-4567"
                    value={formData.contact_phone}
                    onChange={(e) => handleChange('contact_phone', e.target.value)}
                    className="input-default w-full"
                    data-testid="add-event-contact-phone"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_email" className="font-medium text-[#1E3A5F]">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    placeholder="contact@event.com"
                    value={formData.contact_email}
                    onChange={(e) => handleChange('contact_email', e.target.value)}
                    className="input-default w-full"
                    data-testid="add-event-contact-email"
                  />
                </div>
              </div>
            </div>

            {/* Community Sharing */}
            <div className="space-y-6">
              <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] pb-2 border-b-2 border-[#E5E5E5]">
                Community Sharing
              </h2>
              
              <div className="flex items-center justify-between p-4 bg-[#FFEDDF]/50 rounded-xl border border-[#1E3A5F]">
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-[#1E3A5F] mt-0.5" />
                  <div>
                    <Label htmlFor="is_community" className="font-medium text-[#1E3A5F] cursor-pointer">
                      Share as Community Event
                    </Label>
                    <p className="text-sm text-[#4A4A4A] mt-1">
                      Make this event visible to other AMSCompass members
                    </p>
                  </div>
                </div>
                <Switch
                  id="is_community"
                  checked={formData.is_community}
                  onCheckedChange={(checked) => handleChange('is_community', checked)}
                  data-testid="add-event-is-community"
                />
              </div>

              {formData.is_community && (
                <div
                  className="flex items-start gap-3 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl"
                  data-testid="community-share-warning"
                >
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-900">
                    <p className="font-semibold">Heads up — once you share, admins manage this event.</p>
                    <p className="text-amber-800/90 mt-0.5">
                      Sharing puts this event in the public community directory for every AMSCompass
                      vendor. From that point on only AMSCompass admins can edit or delete it. If you
                      spot anything that needs a change later, use{' '}
                      <a
                        href="mailto:support@amscompass.com?subject=Community%20event%20update%20request"
                        className="font-semibold underline"
                      >
                        support@amscompass.com
                      </a>{' '}
                      and we&rsquo;ll review the update.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-6">
              <Button
                type="submit"
                className="btn-primary flex-1"
                disabled={loading}
                data-testid="add-event-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Event
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddEvent;
